import { REFRESH_ACCESS_TOKEN_EXPIRATION_MINUTES } from 'modules/utils/ConfigUtils';
import { createSelector } from 'reselect';
import AuthServices from 'services/AuthServices';
import { currentAccountConsumerSelector } from 'store/account/selectors';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { TpsSource, TpsStatus } from './tpsHelpers';

export const authStateSelector = (state: RootState) => state.authentication;

export const authenticationErrorSelector = createSelector(authStateSelector, auth => auth.error);

export const dfdAuthenticationSelector = createSelector(authStateSelector, auth => auth.dfdAuth);

export const dfdAccessTokenSelector = createSelector(dfdAuthenticationSelector, auth =>
  auth ? auth.accessToken : null
);

export const dfdRefreshTokenSelector = createSelector(dfdAuthenticationSelector, auth =>
  auth ? auth.refreshToken : null
);

export const isDfdAuthenticatingSelector = createSelector(
  authStateSelector,
  auth => auth.isAuthenticating
);

export const protectedRouteModalIsOpenSelector = createSelector(
  authStateSelector,
  auth => auth.protectedRouteModalIsOpen
);

export const hideGuestBenefitsModalSelector = createSelector(
  authStateSelector,
  auth => auth.hideGuestBenefitsModal
);

export const isDfdAuthenticatedSelector = (state: RootState): boolean =>
  AuthServices.isAuthenticated(dfdAuthenticationSelector(state));

export const shallRefreshDfdToken = (state: RootState): boolean =>
  AuthServices.shallRefreshToken(
    dfdAuthenticationSelector(state),
    REFRESH_ACCESS_TOKEN_EXPIRATION_MINUTES
  );

export const fhirCurrentUserPatientId = createSelector(
  currentAccountConsumerSelector,
  currentConsumer => {
    return currentConsumer.cernerPersonId;
  }
);

export const tpsAuthStatusSelector = (state: RootState): TpsStatus[] =>
  oc(state).authentication.tpsAuthStatus([]);

export const tpsAuthFetchingSelector = (state: RootState): boolean =>
  !!state?.authentication?.tpsAuthStatusFetching;

export const tpsAuthErrorSelector = (state: RootState): boolean =>
  !!state?.authentication?.tpsAuthStatusError;

export const createTpsStatusSelector = (source: TpsSource) =>
  createSelector(tpsAuthStatusSelector, authStatus => {
    const sourceObject = authStatus.find(v => v.id === source);

    return sourceObject?.status === 'Authenticated';
  });

export const tpsFhirStatusSelector = createTpsStatusSelector(TpsSource.FHIR);
export const visitPayStatusSelector = createTpsStatusSelector(TpsSource.VISIT_PAY);

export const createTpsTokenSelector = (source: TpsSource) =>
  createSelector(tpsAuthStatusSelector, authStatus => {
    const sourceObject = authStatus.find(v => v.id === source);

    if (sourceObject) {
      return {
        accessToken: sourceObject.accessToken,
        expirationDate: sourceObject.expirationDate
      };
    }

    return null;
  });

export const tpsFhirTokenSelector = createTpsTokenSelector(TpsSource.FHIR);
export const tpsVisitPayTokenSelector = createTpsTokenSelector(TpsSource.VISIT_PAY);

// Auth Lite
export const authLiteSelector = (state: RootState) => state.authLite;
export const authLiteIsFetchingSelector = createSelector(
  authLiteSelector,
  authLite => authLite.isFetching
);
export const authLiteErrorSelector = createSelector(authLiteSelector, authLite => authLite.error);
export const authLiteDataSelector = createSelector(authLiteSelector, authLite => authLite.data);

export const tokenVerifySelector = (state: RootState) => state.tokenVerify;
export const loginTypeSelector = (state: RootState) => state.authentication.loginType;

export type AuthStatus =
  | 'loading'
  | 'unauthenticated'
  | 'auth-lite'
  | 'authenticated'
  | 'loaded'
  | 'error';
export const authNavStateSelector = (state: RootState): AuthStatus => {
  const { isAuthenticating, dfdAuth, error: authError } = state.authentication;
  const { isFetching: appSettingsFetching, error: appSettingsError, shared } = state.appSettings;
  const { isFetching: profileIsFetching, error: profileError, consumer } = state.profile;
  const { isFetching: tokenVerifyFetching, data: tokenVerify } = state.tokenVerify;

  const isFetching =
    isAuthenticating || appSettingsFetching || profileIsFetching || tokenVerifyFetching;
  const isError = !!(appSettingsError || authError || profileError);

  if (isError) {
    return 'error';
  }
  if (isFetching) {
    return 'loading';
  }

  if (tokenVerify?.active === false) {
    return 'unauthenticated';
  }

  if (dfdAuth) {
    return consumer?.consumerId ? 'loaded' : 'authenticated';
  }

  if (shared?.apikey) {
    return 'unauthenticated';
  }

  return 'loading';
};

export const mobileLoadingStepSelector = (state: RootState): string => {
  let step = '1';
  const { error: depsError } = state.global;
  const { dfdAuth, error: authError, tpsAuthStatus = [] } = state.authentication;
  const { error: appSettingsError, shared } = state.appSettings;
  const { error: profileError, consumer } = state.profile;
  const { data: tokenVerify } = state.tokenVerify;

  const { hasFetchedDependencies, termsAndConditions } = state.global;
  const isError = !!(appSettingsError || authError || profileError || depsError);
  const fhirAuth = tpsAuthStatus.find(v => v.id === TpsSource.FHIR);

  if (isError) {
    step = 'error';
  } else if (hasFetchedDependencies) {
    step = '8';
  } else if (consumer?.consumerId) {
    step = '7';
  } else if (fhirAuth?.status === 'Authenticated') {
    step = '6';
  } else if (tokenVerify?.active === true) {
    step = '5';
  } else if (tokenVerify?.active === false) {
    step = '4';
  } else if (tpsAuthStatus.length) {
    step = '3';
  } else if (dfdAuth?.accessToken) {
    step = '2';
  } else if (!!termsAndConditions) {
    step = '1';
  } else if (shared?.apikey) {
    step = '0';
  }

  return `Step ${step} of 8`;
};
